import { useKnowledgeBase } from "../context/KnowledgeBaseContext";
import { useWorkflow } from "../context/WorkflowContext";
import { useBrainBankSync } from "../context/BrainBankSyncContext";

/**
 * What happens to attachments sent from the composer, in one place so the
 * composers and the send path cannot disagree.
 *
 * - "sync": a BrainBank is in use and the switch is on; supported files are
 *   copied to it after Send.
 * - "off": a BrainBank is in use but the user turned the switch off.
 * - "no_permission": the conversation uses a BrainBank this user cannot add to
 *   (a shared project chat). It never appears in their list, so it cannot be
 *   selected; the conversation's pinned id is what gives it away. Only claimed
 *   once the list has loaded without error -- an unloaded or failed list says
 *   nothing about access.
 * - "none": no BrainBank, or a workflow run, which uses none.
 */
export type ComposerSyncMode = "sync" | "off" | "no_permission" | "none";

export const useComposerBrainBankSync = () => {
  const {
    selectedKnowledgeBase,
    knowledgeBasesList,
    loading,
    error,
    pinnedKnowledgeBaseId: pinnedId,
  } = useKnowledgeBase();
  const { selectedWorkflow } = useWorkflow();
  const { saveToBrainBank, setSaveToBrainBank } = useBrainBankSync();

  let mode: ComposerSyncMode = "none";
  if (selectedWorkflow) {
    mode = "none";
  } else if (selectedKnowledgeBase) {
    // Once pinned, only the pinned BrainBank receives files -- whatever is
    // selected, the server answers this chat from that one.
    if (!pinnedId || pinnedId === selectedKnowledgeBase.id) {
      mode = saveToBrainBank ? "sync" : "off";
    }
  } else if (
    pinnedId &&
    !loading &&
    !error &&
    knowledgeBasesList.length > 0 &&
    !knowledgeBasesList.some((knowledgeBase) => knowledgeBase.id === pinnedId)
  ) {
    mode = "no_permission";
  }

  return {
    mode,
    brainBank: mode === "sync" || mode === "off" ? selectedKnowledgeBase : null,
    /** The session already has a BrainBank; the follow-up picker hides. */
    hasPinnedBrainBank: Boolean(pinnedId),
    saveToBrainBank,
    setSaveToBrainBank,
  };
};

export default useComposerBrainBankSync;
